import * as yup from 'yup';
import IconTarget from 'assets/icons/automations/ic-automation-target.svg';
import IconDate from 'assets/icons/automations/ic-automation-date.svg';
import {
  AutomationRequest,
  AutomationType,
  AutomationTypesLabels,
  DateParams,
  TargetWindow,
  TrafficTargetParams
} from 'app/console-api/automations/types';
import { formatDate, parseDate } from 'app/components/utils/date-time';

const trafficTargetParamsSchema = yup.object({
  target: yup
    .number()
    .typeError('Target must be a number')
    .required('Target is required')
    .min(1, 'Target must be greater than 0'),
  window: yup
    .mixed<TargetWindow>()
    .oneOf(Object.values(TargetWindow) as TargetWindow[], 'Invalid window')
    .required('Window is required')
});

const dateParamsSchema = yup.object({
  at: yup
    .string()
    .required('Date is required')
    .test('is-iso', 'Date must be a valid date', (v) => !!v && !!parseDate(v))
});

type AutomationConfig<TParams> = {
  type: AutomationType;
  label: string;
  icon: string;
  getDefaultParams: () => TParams;
  describe: (params: TParams) => string;
  // validator is optional but handy for local validation
  validator?: yup.ObjectSchema<any>;
  // control?: React.FC<AutomationTypeControlProps<TParams>>;
};

export const AutomationRegistry: Record<AutomationType, AutomationConfig<any>> = {
  [AutomationType.TrafficTarget]: {
    type: AutomationType.TrafficTarget,
    label: AutomationTypesLabels[AutomationType.TrafficTarget],
    icon: IconTarget,
    getDefaultParams: (): TrafficTargetParams => ({ target: 100, window: TargetWindow.Quartile }),
    describe: (p: TrafficTargetParams) =>
      `Stop after reaching ${p.target} successful claims in total per ${p.window}.`,
    validator: trafficTargetParamsSchema
    // control: TrafficTargetControl,
  },

  [AutomationType.DateStart]: {
    type: AutomationType.DateStart,
    label: AutomationTypesLabels[AutomationType.DateStart],
    icon: IconDate,
    getDefaultParams: (): DateParams => ({ at: formatDate(new Date()) }),
    describe: (p: DateParams) => `Start at ${p.at}.`,
    validator: dateParamsSchema
    // control: DateControl,
  },

  [AutomationType.DateEnd]: {
    type: AutomationType.DateEnd,
    label: AutomationTypesLabels[AutomationType.DateEnd],
    icon: IconDate,
    getDefaultParams: (): DateParams => ({ at: formatDate(new Date()) }),
    describe: (p: DateParams) => `End at ${p.at}.`,
    validator: dateParamsSchema
    // control: DateControl,
  }
};

// Small helpers
export const getIcon = (type: AutomationType) => AutomationRegistry[type].icon;
export const getLabel = (type: AutomationType) => AutomationRegistry[type].label;
export const getDefaultParamsByType = (type: AutomationType) =>
  AutomationRegistry[type].getDefaultParams();

export const describeAutomation = (a: AutomationRequest): string => {
  const cfg = AutomationRegistry[a.type];
  return cfg.describe(a.params);
};
